import { DateInputValue, FrequencyValue } from "basis";
import { SearchAddress } from "components/AutoAddress/api/addressApi";
import { OptionsValues } from "components/types";
import { ActionMap } from "library/ActionMap";
import { stateOptions, yesNoOptions } from "library/sharedOptions";
import { MakeRequired } from "library/utils";
import { liabilityTypeOptions } from "../Liabilities/liabilityForm";
import {
  applyingOnBehalfOfOtherOption,
  durationOfHardshipOptions,
  employmentStatusOptions,
  primaryReasonOptions,
  secondaryReasonOptions,
  trueFalseOptions,
} from "../options";
import {
  accountTypeOptionsAu,
  accountTypeOptionsNz,
} from "../YourAccounts/yourAccountsForm";

export type PrescreeningState = {
  applyingOnBehalfOfOther: OptionsValues<typeof applyingOnBehalfOfOtherOption>;
  authorizedThirdPartyForm: boolean;
  onHardshipInLast24Months: OptionsValues<typeof trueFalseOptions>;
  authorisedToActOnBehalf: OptionsValues<typeof yesNoOptions>;
  durationOfHardship: OptionsValues<typeof durationOfHardshipOptions>;
};

export type AddressMode = "auto" | "manual";

export type AddressState = {
  auto: SearchAddress;
  mode: AddressMode;
  streetAddress: string;
  suburb: string;
  city: string;
  state: OptionsValues<typeof stateOptions>;
  postCode: string;
};

export interface PersonalDetailsState {
  authorisedPersonDetails?: {
    fullName: string;
    counsellorName: string;
    email: string;
    contactNumber: string;
  };
  applicantDetails: {
    firstName: string;
    lastName: string;
    dateOfBirth: DateInputValue;
    email: string;
    contactNumber: string;
    address: AddressState;
    employmentStatus: OptionsValues<typeof employmentStatusOptions>;
  };
}

export interface ReasonsForApplyingState {
  primaryReason: OptionsValues<typeof primaryReasonOptions>;
  secondaryReason: OptionsValues<typeof secondaryReasonOptions>;
  startDate: DateInputValue;
  situation: string;
}

export interface YourIncomeState {
  salary: FrequencyValue;
  wages: FrequencyValue;
  centrelink?: FrequencyValue;
  businessIncome: FrequencyValue;
  other: FrequencyValue;
  winzBenefit?: FrequencyValue;
  irdFamilyTaxCredit?: FrequencyValue;
  irdChildSupport?: FrequencyValue;
  rentBoard?: FrequencyValue;
  investment?: FrequencyValue;
}

export interface ExpensesState {
  residential: {
    foodGroceriesHousehold: FrequencyValue;
    councilRates: FrequencyValue;
    electricity: FrequencyValue;
    gas: FrequencyValue;
    water: FrequencyValue;
    landlineInternetCableTV: FrequencyValue;
  };
  other: {
    mobilePhone: FrequencyValue;
    medicalPharmaceutical: FrequencyValue;
    clothesShoes: FrequencyValue;
    petrol: FrequencyValue;
    carRegistrationMaintenance: FrequencyValue;
    publicTransport: FrequencyValue;
    childCare: FrequencyValue;
    childMaintenance: FrequencyValue;
    educationExpenses: FrequencyValue;
    otherHobbies: FrequencyValue;
    homeContentsInsurance: FrequencyValue;
    carInsurance: FrequencyValue;
    healthInsurance: FrequencyValue;
    lifeInsurance: FrequencyValue;
    otherInsurance: FrequencyValue;
    donations?: FrequencyValue;
  };
}
export type Account = {
  accountType: OptionsValues<
    typeof accountTypeOptionsAu | typeof accountTypeOptionsNz
  >;
  accountNumber: string;
  monthlyRepayments: string;
};

export type YourAccountsState = {
  accounts: Account[];
};

export type Liability = {
  liabilityType: OptionsValues<typeof liabilityTypeOptions>;
  financialInstitution: string;
  balanceOwing: string;
  minimumRepayment?: FrequencyValue;
  hadAppliedForAssistance: OptionsValues<typeof yesNoOptions>;
  creditLimit?: string;
};

export type LiabilitiesState = {
  hadOtherMortgages: OptionsValues<typeof yesNoOptions>;
  liabilities: Liability[];
};

export type ReviewState = {
  acknowledge: {
    shoppingBlock: boolean;
    debitCardAgreement: boolean;
    termsAndConditions: boolean;
    privacyPolicy: boolean;
    maintainingPayments: boolean;
    revisedLoan: boolean;
  };
  ticketId: number;
};

export type HardshipState = {
  isStarted: boolean;
  acknowledged: boolean;
  prescreening?: PrescreeningState;
  personalDetails: PersonalDetailsState;
  yourAccounts?: YourAccountsState;
  reasonsForApplying?: ReasonsForApplyingState;
  yourIncome?: YourIncomeState;
  expenses?: ExpensesState;
  liabilities?: LiabilitiesState;
  review?: ReviewState;
};

export enum ActionTypes {
  START_APPLICATION = "START_APPLICATION",
  COMPLETE_PRESCREENING = "COMPLETE_PRESCREENING",
  COMPLETE_PERSONAL_DETAILS = "COMPLETE_PERSONAL_DETAILS",
  COMPLETE_YOUR_ACCOUNTS = "COMPLETE_YOUR_ACCOUNTS",
  COMPLETE_YOUR_INCOME = "COMPLETE_YOUR_INCOME",
  COMPLETE_REASONS_FOR_APPLYING = "COMPLETE_REASONS_FOR_APPLYING",
  COMPLETE_EXPENSES = "COMPLETE_EXPENSES",
  COMPLETE_LIABILITIES = "COMPLETE_LIABILITIES",
  SUBMIT_SUCCESS = "SUBMIT_SUCCESS",
}

export type Payloads = {
  [ActionTypes.COMPLETE_PRESCREENING]: PrescreeningState;
  [ActionTypes.COMPLETE_PERSONAL_DETAILS]: PersonalDetailsState;
  [ActionTypes.COMPLETE_YOUR_ACCOUNTS]: YourAccountsState;
  [ActionTypes.COMPLETE_YOUR_INCOME]: YourIncomeState;
  [ActionTypes.COMPLETE_REASONS_FOR_APPLYING]: ReasonsForApplyingState;
  [ActionTypes.COMPLETE_EXPENSES]: ExpensesState;
  [ActionTypes.COMPLETE_LIABILITIES]: LiabilitiesState;
  [ActionTypes.SUBMIT_SUCCESS]: { ticketId: number };
};

export type Actions = ActionMap<ActionTypes, Payloads>;

export const shortTermRequiredFields = [
  "isStarted",
  "prescreening",
  "acknowledged",
  "personalDetails",
  "reasonsForApplying",
  "yourAccounts",
  "review",
] as const;

export type LongTermCompleteState = Required<HardshipState>;
export type ShortTermCompleteState = MakeRequired<
  HardshipState,
  typeof shortTermRequiredFields[number]
>;

export type HardshipApplicationParam =
  | {
      isProduction: boolean;
      isNZ: boolean;
      isLongTerm: true;
      fields: LongTermCompleteState;
    }
  | {
      isProduction: boolean;
      isNZ: boolean;
      isShortTerm: true;
      fields: ShortTermCompleteState;
    };
